import {
  ApiWorkflowEventTypes,
  getApiWorkflowEventTypesTitle,
} from "@/types/workflows/workflow.base-enum.types.ts";
import {
  ApiWorkflowCondition,
  ApiWorkflowConditionEntityTypes,
  ApiWorkflowConditionFieldTypes,
  ApiWorkflowConditionOperator,
  getApiWorkflowConditionFieldTypesTitle,
} from "@/types/workflows/workflow.condition.types.ts";
import {
  Avatar,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

interface WorkflowTriggerConditionBlockProps {
  condition: ApiWorkflowCondition;
  onEdit?: () => void;
  onDelete?: () => void;
}

const employeeEntityConditionFields = new Set<ApiWorkflowConditionFieldTypes>([
  ApiWorkflowConditionFieldTypes.employee_position_department_id,
  ApiWorkflowConditionFieldTypes.employee_position_division_id,
  ApiWorkflowConditionFieldTypes.employee_employment_legal_entity_id,
  ApiWorkflowConditionFieldTypes.employee_position_location_id,
  ApiWorkflowConditionFieldTypes.employee_position_job_profile_id,
]);

const candidateEntityConditionFields = new Set<ApiWorkflowConditionFieldTypes>([
  ApiWorkflowConditionFieldTypes.candidate_stage_id,
  ApiWorkflowConditionFieldTypes.candidate_previous_stage_id,
  ApiWorkflowConditionFieldTypes.candidate_pipeline_id,
  ApiWorkflowConditionFieldTypes.candidate_disqualification_reason_id,
]);

const jobEntityConditionFields = new Set<ApiWorkflowConditionFieldTypes>([
  ApiWorkflowConditionFieldTypes.job_id,
  ApiWorkflowConditionFieldTypes.job_department_id,
  ApiWorkflowConditionFieldTypes.job_division_id,
  ApiWorkflowConditionFieldTypes.job_location_id,
]);

export const WorkflowTriggerConditionBlock = ({
  condition,
  onEdit,
  onDelete,
}: WorkflowTriggerConditionBlockProps) => {
  const { t } = useTranslation();
  const withOperator = (condition: ApiWorkflowCondition, title: string) => {
    switch (condition.operator) {
      case ApiWorkflowConditionOperator.not_in:
        return `${t("workflow:operator_not_in")}: ${title}`;
      case ApiWorkflowConditionOperator.is_null:
        return t("workflow:operator_is_null");
      case ApiWorkflowConditionOperator.is_not_null:
        return t("workflow:operator_is_not_null");
      default:
        return title;
    }
  };

  const getTitle = (condition: ApiWorkflowCondition) => {
    if (condition.entity_type == ApiWorkflowConditionEntityTypes.event) {
      return getApiWorkflowEventTypesTitle(condition.field_value.value as ApiWorkflowEventTypes);
    }

    if (condition.field_name == ApiWorkflowConditionFieldTypes.time_off_category_id) {
      const title =
        condition.condition_entities.length === 0
          ? t("common:all_categories")
          : condition.condition_entities.map((entity) => entity.title).join(", ");

      return withOperator(condition, title);
    }

    if (
      employeeEntityConditionFields.has(condition.field_name) ||
      candidateEntityConditionFields.has(condition.field_name) ||
      jobEntityConditionFields.has(condition.field_name)
    ) {
      return withOperator(
        condition,
        condition.condition_entities.map((entity) => entity.title).join(", ")
      );
    }
  };

  const getIcon = (condition: ApiWorkflowCondition) => {
    if (condition.entity_type == ApiWorkflowConditionEntityTypes.event) {
      if (
        (condition.field_value.value as ApiWorkflowEventTypes) ==
        ApiWorkflowEventTypes.time_off_request_approved
      ) {
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-umbrella-beach"} />} />;
      }
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-circle-user"} />} />;
    }
    if (employeeEntityConditionFields.has(condition.field_name)) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-circle-user"} />} />;
    }
    if (candidateEntityConditionFields.has(condition.field_name)) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-user-tie"} />} />;
    }
    if (jobEntityConditionFields.has(condition.field_name)) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-briefcase"} />} />;
    }
    if (condition.field_name == ApiWorkflowConditionFieldTypes.time_off_category_id) {
      return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-island-tropical"} />} />;
    }
    return null;
  };

  return (
    <ListItem
      icon={getIcon(condition)}
      behavior={"card"}
      preTitle={getApiWorkflowConditionFieldTypesTitle(condition.field_name)}
      title={getTitle(condition)}
      valueSlot={
        (onEdit || onDelete) && (
          <DropdownMenu>
            {onEdit && (
              <DropdownMenuItem
                title={t("common:edit")}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                onClick={onEdit}
              />
            )}
            {onDelete && (
              <DropdownMenuItem
                title={t("common:delete")}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                variant={"menuDanger"}
                onClick={onDelete}
              />
            )}
          </DropdownMenu>
        )
      }
    />
  );
};
